import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TypewriterText } from '../hooks/useTypewriter';

const API = '/api';

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d`;
  const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo`;
  return `${Math.floor(mo / 12)}y`;
}

export default function Blog() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isAdmin = user?.role === 'admin';

  const authHeaders = () => {
    const h = { 'Content-Type': 'application/json' };
    const t = localStorage.getItem('logos_session_token');
    if (t) h['Authorization'] = `Bearer ${t}`;
    return h;
  };

  useEffect(() => {
    fetch(`${API}/blog?limit=200`)
      .then(r => r.ok ? r.json() : [])
      .then(setPosts)
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/blog`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ body: text })
      });
      if (res.ok) {
        const p = await res.json();
        setPosts([p, ...posts]);
        setBody('');
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'failed to post');
      }
    } catch { setError('failed to post'); }
    finally { setSubmitting(false); }
  };

  const remove = async (post_id) => {
    if (!window.confirm('delete this entry?')) return;
    const prev = posts;
    setPosts(posts.filter(p => p.post_id !== post_id));
    const res = await fetch(`${API}/blog/${post_id}`, {
      method: 'DELETE', credentials: 'include', headers: authHeaders()
    });
    if (!res.ok) setPosts(prev);
  };

  return (
    <div className="min-h-screen bg-black py-12 lg:py-20 px-6 lg:px-8 page-wrapper" data-testid="blog-page">
      <div className="max-w-2xl mx-auto space-y-8">
        <header className="flex items-baseline justify-between">
          <h1 className="text-3xl lg:text-4xl font-bold text-white lowercase"><TypewriterText text="log" speed={90} startDelay={150} /></h1>
          <a href="/api/blog.html" target="_blank" rel="noreferrer" className="text-[#555555] hover:text-white transition-colors text-xs lowercase" data-testid="blog-html-link">
            html view →
          </a>
        </header>

        {isAdmin && (
          <form onSubmit={submit} className="space-y-2" data-testid="blog-composer">
            <textarea
              value={body}
              onChange={e => setBody(e.target.value.slice(0, 50000))}
              placeholder="paste, type, append. markdown-ish: # heading, **bold**, *italic*, `code`, [text](url). blank lines = paragraph."
              rows={6}
              className="w-full bg-[#0e120f] border border-[#272e28] text-white lowercase px-4 py-3 rounded-lg outline-none focus:border-[#4a514a] transition-colors resize-y text-sm leading-relaxed"
              data-testid="blog-body"
            />
            <div className="flex items-center justify-between">
              <span className="text-[#333333] text-xs lowercase">{body.length}/50000</span>
              <button
                type="submit"
                disabled={!body.trim() || submitting}
                className="border border-[#383f38] px-5 py-1.5 text-white lowercase text-sm hover:bg-white hover:text-black transition-all duration-300 rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="blog-submit"
              >
                {submitting ? 'appending…' : 'append'}
              </button>
            </div>
            {error && <p className="text-white/70 lowercase text-sm">{error}</p>}
          </form>
        )}

        {loading ? (
          <p className="text-[#555555] lowercase text-sm animate-pulse">loading…</p>
        ) : posts.length === 0 ? (
          <p className="text-[#555555] lowercase text-sm">nothing here yet.</p>
        ) : (
          <ol className="space-y-10" data-testid="blog-list">
            {posts.map(p => (
              <li key={p.post_id} className="space-y-2" data-testid={`blog-post-${p.post_id}`}>
                <div className="flex items-center gap-3 text-xs">
                  <time className="text-[#555555] lowercase tabular-nums" dateTime={p.created_at}>
                    {(p.created_at || '').slice(0, 10)} · {timeAgo(p.created_at)}
                  </time>
                  {isAdmin && (
                    <button onClick={() => remove(p.post_id)} className="text-[#333333] hover:text-white transition-colors lowercase" data-testid={`delete-post-${p.post_id}`}>
                      delete
                    </button>
                  )}
                </div>
                <div className="text-[#cccccc] lowercase text-sm leading-relaxed whitespace-pre-wrap break-words">
                  {p.body}
                </div>
              </li>
            ))}
          </ol>
        )}

        <div className="pt-6 border-t border-[#181e19]">
          <Link to="/info" className="text-[#333333] hover:text-white transition-colors text-xs lowercase">← info</Link>
        </div>
      </div>
    </div>
  );
}
